// ==========================================
// src/pages/Perfil.jsx
// PERFIL DO MOTORISTA/EMPRESA - VERSÃO CORRIGIDA
// ✅ FIX: Foto do motorista persiste ao sair e voltar (usa ID do localStorage)
// ✅ FIX: Empresa continua igual (logo no Supabase + localStorage)
// ==========================================

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { 
  User, Truck, LogOut, MapPin, Award, 
  Trophy, Gift, Star, Zap,
  CheckCircle2, Search, LocateFixed,
  Package, Loader2, ArrowRight, X,
  Building2, Image as ImageIcon, Upload, Camera,
  Phone, Home, Hash, Navigation, Edit3, Save, Plus, Trash2,
  CreditCard, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from 'framer-motion';
import CargaCard from '@/components/Cargas/CargaCard';
import { useNivelMotorista, NIVEIS_MOTORISTA } from '@/lib/nivelMotorista';
import { supabase } from '@/lib/supabase';

// ==========================================
// CONFIGURAÇÃO BORAFRETE
// ==========================================
const WHATSAPP_BORAFRETE = "5565981181997";

// ==========================================
// ESTADOS DO BRASIL
// ==========================================
const ESTADOS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", 
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", 
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

// ==========================================
// TIPOS DE VEÍCULO (para filtro)
// ==========================================
const TIPOS_VEICULO = ['Carreta', 'Truck', 'Bitrem', 'Toco', 'VUC', 'Furgão', 'Prancha', 'Graneleiro', 'Frigorífico', 'Cavalo Mecânico'];

// ==========================================
// FRASES MOTIVACIONAIS POR NÍVEL
// ==========================================
const FRASES_POR_NIVEL = {
  bronze: "🚛 Cada km com a BoraFrete é uma conquista! Continue rumo ao ouro!",
  prata: "⭐ Você está indo muito bem! Rumo ao ouro!",
  ouro: "🎉 Parabéns! Você já pode participar dos sorteios!",
  diamante: "💎 Nível Diamante! Você é um motorista de elite!",
  black: "🏆 Black Diamante! O topo da montanha!"
};

// ==========================================
// 🗄️ HELPERS DE STORAGE OTIMIZADO
// ==========================================

const getArquivos = () => {
  try {
    const data = localStorage.getItem('borafrete_arquivos');
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Erro ao ler arquivos:', error);
    return {};
  }
};

const saveArquivos = (arquivos) => {
  try {
    localStorage.setItem('borafrete_arquivos', JSON.stringify(arquivos));
    return true;
  } catch (error) {
    console.error('Erro ao salvar arquivos:', error);
    if (error.name === 'QuotaExceededError') {
      toast.error('Limite de armazenamento excedido. Use arquivos menores.');
    }
    return false;
  }
};

// ==========================================
// 🗄️ HELPERS PARA PLANOS DE COMPENSAÇÃO
// ==========================================

const getPlanosCompensacao = () => {
  try {
    const data = localStorage.getItem('borafrete_planos_compensacao');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Erro ao ler planos de compensação:', error);
    return [];
  }
};

const savePlanosCompensacao = (planos) => {
  try {
    localStorage.setItem('borafrete_planos_compensacao', JSON.stringify(planos));
    return true;
  } catch (error) {
    console.error('Erro ao salvar planos de compensação:', error);
    return false;
  }
};

// ==========================================
// ✅ FUNÇÃO PARA PEGAR INICIAIS DO NOME
// ==========================================
const getIniciais = (nome) => {
  if (!nome) return 'E';
  const palavras = nome.trim().split(' ');
  if (palavras.length === 1) return palavras[0].substring(0, 2).toUpperCase();
  return (palavras[0][0] + palavras[palavras.length - 1][0]).toUpperCase();
};

// ✅ FUNÇÃO PARA FORMATAR CNPJ
const formatarCNPJ = (cnpj) => {
  if (!cnpj) return 'Não informado';
  const numeros = cnpj.replace(/\D/g, '');
  if (numeros.length !== 14) return cnpj;
  return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-${numeros.slice(12, 14)}`;
};

// ✅ FUNÇÃO PARA FORMATAR CPF
const formatarCPF = (cpf) => {
  if (!cpf) return 'Não informado';
  const numeros = cpf.replace(/\D/g, '');
  if (numeros.length !== 11) return cpf;
  return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-${numeros.slice(9, 11)}`;
};

// ✅ FUNÇÃO PARA FORMATAR TELEFONE
const formatarTelefone = (tel) => {
  if (!tel) return 'Não informado';
  const numeros = tel.replace(/\D/g, '');
  if (numeros.length === 11) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
  }
  if (numeros.length === 10) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6, 10)}`;
  }
  return tel;
};

// ✅ FUNÇÃO PARA FORMATAR CEP
const formatarCEP = (cep) => {
  if (!cep) return 'Não informado';
  const numeros = cep.replace(/\D/g, '');
  if (numeros.length === 8) return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
  return cep;
};

// ✅ FUNÇÃO PARA FORMATAR PLACA
const formatarPlaca = (placa) => {
  if (!placa) return 'Não informado';
  const limpo = placa.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (limpo.length === 7) {
    return `${limpo.slice(0, 3)}-${limpo.slice(3)}`;
  }
  return placa.toUpperCase();
};

const Perfil = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  
  // SISTEMA UNIFICADO DE NÍVEIS
  const { 
    entregas: entregasRealizadas, 
    nivel: nivelAtual, 
    progresso, 
    proximoNivel 
  } = useNivelMotorista(user);
  
  // Estados
  const [fraseDoDia, setFraseDoDia] = useState('');
  const [showLocalizacao, setShowLocalizacao] = useState(false);
  const [estadoSelecionado, setEstadoSelecionado] = useState('');
  const [cidadeDigitada, setCidadeDigitada] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [cargasSistema, setCargasSistema] = useState([]);
  const [cargasFiltradas, setCargasFiltradas] = useState([]);
  const [loadingCargas, setLoadingCargas] = useState(true);
  const [filtroVeiculo, setFiltroVeiculo] = useState('');

  // Estado para foto do motorista ou logo da empresa
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [isUploadingFoto, setIsUploadingFoto] = useState(false);

  // Estado para planos de compensação
  const [planosCompensacao, setPlanosCompensacao] = useState([]);

  // ✅ ESTADO para dados REAIS do motorista (da tabela motoristas)
  const [dadosMotorista, setDadosMotorista] = useState({
    nome_completo: '',
    cpf: '',
    telefone: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    placa_caminhao: '',
    cnh: '',
    categoria_cnh: ''
  });

  // ✅ ESTADOS PARA EDIÇÃO DE DADOS DO MOTORISTA (estrutura real do Supabase)
  const [editandoEndereco, setEditandoEndereco] = useState(false);
  const [editandoContato, setEditandoContato] = useState(false);
  const [editandoCaminhao, setEditandoCaminhao] = useState(false);
  const [editandoCNH, setEditandoCNH] = useState(false);
  
  // ✅ ESTRUTURA REAL DO SUPABASE (colunas existentes)
  const [endereco, setEndereco] = useState({
    endereco: '',      // campo único no Supabase
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: ''
  });
  
  const [contato, setContato] = useState({
    telefone: ''       // só 1 campo no Supabase
  });
  
  const [caminhao, setCaminhao] = useState({
    placa_caminhao: ''  // só 1 placa no Supabase
  });
  
  const [cnh, setCnh] = useState({
    cnh: '',
    categoria_cnh: ''
  });

  // Ref para armazenar arquivo em memória
  const fileRef = useRef(null);

  // Verificar tipo de usuário
  const isMotorista = user?.tipo === 'motorista' || (!user?.cnpj && user?.cpf);
  const isEmpresa = user?.tipo === 'empresa' || user?.cnpj;

  // Atualiza frase baseada no nível
  useEffect(() => {
    setFraseDoDia(FRASES_POR_NIVEL[nivelAtual?.id] || FRASES_POR_NIVEL.bronze);
  }, [nivelAtual]);

  // ✅ CARREGAR DADOS REAIS DO MOTORISTA DO SUPABASE
  useEffect(() => {
    if (isMotorista && user?.id) {
      carregarDadosMotorista();
    }
  }, [isMotorista, user?.id]);

  const carregarDadosMotorista = async () => {
    try {
      const { data, error } = await supabase
        .from('motoristas')
        .select('nome_completo, cpf, telefone, endereco, numero, bairro, cidade, estado, cep, placa_caminhao, cnh, categoria_cnh')
        .eq('usuario_id', user.id)
        .single();

      if (data) {
        // ✅ Salva os dados REAIS do motorista
        setDadosMotorista({
          nome_completo: data.nome_completo || '',
          cpf: data.cpf || '',
          telefone: data.telefone || '',
          endereco: data.endereco || '',
          numero: data.numero || '',
          bairro: data.bairro || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          cep: data.cep || '',
          placa_caminhao: data.placa_caminhao || '',
          cnh: data.cnh || '',
          categoria_cnh: data.categoria_cnh || ''
        });

        // ✅ Atualiza os estados de edição também
        setEndereco({
          endereco: data.endereco || '',
          numero: data.numero || '',
          bairro: data.bairro || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          cep: data.cep || ''
        });
        
        setContato({
          telefone: data.telefone || ''
        });
        
        setCaminhao({
          placa_caminhao: data.placa_caminhao || ''
        });
        
        setCnh({
          cnh: data.cnh || '',
          categoria_cnh: data.categoria_cnh || ''
        });
      }
    } catch (err) {
      console.error('Erro ao carregar dados do motorista:', err);
    }
  };

  // Listener para sincronizar em tempo real
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'borafrete_contratos') {
        console.log('🔄 Perfil: Contratos atualizados, recalculando nível...');
        window.dispatchEvent(new Event('storage-update'));
      }
      if (e.key === 'borafrete_planos_compensacao') {
        console.log('🔄 Perfil: Planos de compensação atualizados');
        const planos = getPlanosCompensacao();
        setPlanosCompensacao(planos);
      }
    };
    
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // ==========================================
  // ✅ CORRIGIDO: Pega ID do localStorage (mesmo do AuthContext)
  // ==========================================
  const getUserIdSync = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('borafrete_user') || '{}');
      return storedUser.id || user?.id;
    } catch {
      return user?.id;
    }
  };

  // ==========================================
  // ✅ CORRIGIDO: Carrega foto/logo usando ID do localStorage
  // ==========================================
  useEffect(() => {
    const userId = getUserIdSync();
    if (!userId) {
      console.log('[Perfil] Sem userId, não carrega foto');
      return;
    }

    console.log('[Perfil] Carregando foto. userId:', userId, 'isMotorista:', isMotorista, 'isEmpresa:', isEmpresa);

    const arquivos = getArquivos();
    const userArquivos = arquivos[userId] || {};

    if (isEmpresa) {
      // EMPRESA: mantém igual (Supabase primeiro, localStorage fallback)
      if (user?.logo_url) {
        console.log('[Perfil] Logo do Supabase (empresa)');
        setFotoPerfil({ 
          data: user.logo_url, 
          tipo: 'logo',
          fonte: 'supabase'
        });
      } else if (userArquivos.logo) {
        console.log('[Perfil] Logo do localStorage (empresa)');
        setFotoPerfil({ ...userArquivos.logo, tipo: 'logo' });
      } else {
        setFotoPerfil(null);
      }
    } else if (isMotorista) {
      // MOTORISTA: busca foto_perfil no ID correto do localStorage
      if (userArquivos.foto_perfil) {
        console.log('[Perfil] Foto encontrada no localStorage (motorista)');
        setFotoPerfil({ ...userArquivos.foto_perfil, tipo: 'foto' });
      } else {
        console.log('[Perfil] Nenhuma foto encontrada para motorista');
        setFotoPerfil(null);
      }
    }

    // Carregar planos de compensação
    const planos = getPlanosCompensacao();
    setPlanosCompensacao(planos);
  }, [user?.id, isMotorista, isEmpresa, user?.logo_url]);

  // BUSCAR CARGAS REAIS DO SISTEMA
  useEffect(() => {
    const buscarCargasReais = () => {
      setLoadingCargas(true);
      
      setTimeout(() => {
        const cargasLocalStorage = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
        console.log(`📦 Perfil: ${cargasLocalStorage.length} cargas do localStorage`);
        setCargasSistema(cargasLocalStorage);
        setLoadingCargas(false);
      }, 600);
    };

    buscarCargasReais();
  }, []);

  // FILTRAR CARGAS
  useEffect(() => {
    if (!estadoSelecionado || cargasSistema.length === 0) {
      setCargasFiltradas([]);
      return;
    }

    let filtradas = cargasSistema.filter(carga => {
      const matchEstado = carga.origem_estado === estadoSelecionado;
      
      let matchCidade = true;
      if (cidadeDigitada.trim()) {
        const cidadeBusca = cidadeDigitada.toLowerCase().trim();
        matchCidade = carga.origem_cidade?.toLowerCase().includes(cidadeBusca);
      }
      
      let matchVeiculo = true;
      if (filtroVeiculo) {
        matchVeiculo = carga.tipo_veiculo?.toLowerCase() === filtroVeiculo.toLowerCase();
      }
      
      const disponivel = carga.status === 'disponivel' || !carga.status;
      
      return matchEstado && matchCidade && matchVeiculo && disponivel;
    });

    setCargasFiltradas(filtradas);
  }, [estadoSelecionado, cidadeDigitada, filtroVeiculo, cargasSistema]);

  // ==========================================
  // ✅ CORRIGIDO: Upload salva foto no ID correto do motorista
  // ==========================================
  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 2MB');
      return;
    }

    // Validar tipo
    const tiposValidos = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!tiposValidos.includes(file.type)) {
      toast.error('Apenas imagens JPEG/PNG são permitidas');
      return;
    }

    setIsUploadingFoto(true);
    try {
      // Converte para base64
      const base64 = await fileToBase64(file);
      
      const novaFoto = {
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        data: base64,
        hasFile: true
      };

      // ✅ USA ID DO LOCALSTORAGE (mesmo do AuthContext)
      const userId = getUserIdSync();
      console.log('[Perfil] Salvando foto para userId:', userId);

      const arquivos = getArquivos();
      if (!arquivos[userId]) {
        arquivos[userId] = {};
      }
      
      if (isEmpresa) {
        // EMPRESA: mantém igual (salva logo + Supabase)
        arquivos[userId].logo = novaFoto;
        setFotoPerfil({ ...novaFoto, tipo: 'logo' });
        
        // Tenta salvar no Supabase também (empresa)
        try {
          const { error: updateError } = await supabase
            .from('empresas')
            .update({ logo_url: base64 })
            .eq('usuario_id', userId);

          if (updateError) {
            console.error('❌ Erro ao salvar logo no Supabase:', updateError);
          } else {
            console.log('✅ Logo salvo no Supabase!');
            await refreshUser();
          }
        } catch (err) {
          console.warn('Erro Supabase logo:', err);
        }
      } else {
        // MOTORISTA: salva foto_perfil no ID correto do localStorage
        arquivos[userId].foto_perfil = novaFoto;
        setFotoPerfil({ ...novaFoto, tipo: 'foto' });
        console.log('✅ Foto salva no localStorage (motorista):', userId);
      }
      
      saveArquivos(arquivos);
      toast.success(isEmpresa ? 'Logo atualizado!' : 'Foto atualizada!');

    } catch (err) {
      console.error('Erro ao processar imagem:', err);
      toast.error('Erro ao processar imagem');
    } finally {
      setIsUploadingFoto(false);
    }
  };

  // Helper para converter File para base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  // ==========================================
  // ✅ CORRIGIDO: Remove foto usando ID do localStorage
  // ==========================================
  const handleRemoverFoto = async () => {
    const confirmMsg = isEmpresa 
      ? 'Tem certeza que deseja remover o logo?'
      : 'Tem certeza que deseja remover sua foto?';
      
    if (!confirm(confirmMsg)) return;
    
    // ✅ USA ID DO LOCALSTORAGE
    const userId = getUserIdSync();
    
    // Remove do Supabase (empresa)
    if (isEmpresa && userId) {
      try {
        const { error } = await supabase
          .from('empresas')
          .update({ logo_url: null })
          .eq('usuario_id', userId);

        if (error) {
          console.error('❌ Erro ao remover logo do Supabase:', error);
        } else {
          console.log('✅ Logo removido do Supabase');
          await refreshUser();
        }
      } catch (err) {
        console.warn('Erro ao remover do Supabase:', err);
      }
    }
    
    // Remove do localStorage (motorista e empresa)
    const arquivos = getArquivos();
    if (arquivos[userId]) {
      if (isEmpresa) {
        delete arquivos[userId].logo;
      } else {
        delete arquivos[userId].foto_perfil;
      }
      saveArquivos(arquivos);
    }
    
    setFotoPerfil(null);
    fileRef.current = null;
    toast.info(isEmpresa ? 'Logo removido' : 'Foto removida');
  };

  // ✅ HANDLERS PARA EDITAR DADOS DO MOTORISTA (colunas REAIS do Supabase)
  const handleSalvarEndereco = async () => {
    try {
      const { error } = await supabase
        .from('motoristas')
        .update({
          endereco: endereco.endereco,    // campo único no Supabase
          numero: endereco.numero,
          bairro: endereco.bairro,
          cidade: endereco.cidade,
          estado: endereco.estado,
          cep: endereco.cep
        })
        .eq('usuario_id', user.id);

      if (error) throw error;
      
      // ✅ Atualiza os dados locais também
      setDadosMotorista(prev => ({
        ...prev,
        endereco: endereco.endereco,
        numero: endereco.numero,
        bairro: endereco.bairro,
        cidade: endereco.cidade,
        estado: endereco.estado,
        cep: endereco.cep
      }));
      
      toast.success('Endereço atualizado com sucesso!');
      setEditandoEndereco(false);
      await refreshUser();
    } catch (err) {
      console.error('Erro ao salvar endereço:', err);
      toast.error('Erro ao salvar endereço');
    }
  };

  const handleSalvarContato = async () => {
    try {
      const { error } = await supabase
        .from('motoristas')
        .update({
          telefone: contato.telefone    // só 1 campo no Supabase
        })
        .eq('usuario_id', user.id);

      if (error) throw error;
      
      // ✅ Atualiza os dados locais também
      setDadosMotorista(prev => ({
        ...prev,
        telefone: contato.telefone
      }));
      
      toast.success('Telefone atualizado com sucesso!');
      setEditandoContato(false);
      await refreshUser();
    } catch (err) {
      console.error('Erro ao salvar telefone:', err);
      toast.error('Erro ao salvar telefone');
    }
  };

  const handleSalvarCaminhao = async () => {
    try {
      const { error } = await supabase
        .from('motoristas')
        .update({
          placa_caminhao: caminhao.placa_caminhao    // só 1 placa no Supabase
        })
        .eq('usuario_id', user.id);

      if (error) throw error;
      
      // ✅ Atualiza os dados locais também
      setDadosMotorista(prev => ({
        ...prev,
        placa_caminhao: caminhao.placa_caminhao
      }));
      
      toast.success('Placa do caminhão atualizada!');
      setEditandoCaminhao(false);
      await refreshUser();
    } catch (err) {
      console.error('Erro ao salvar caminhão:', err);
      toast.error('Erro ao salvar caminhão');
    }
  };

  const handleSalvarCNH = async () => {
    try {
      const { error } = await supabase
        .from('motoristas')
        .update({
          cnh: cnh.cnh,
          categoria_cnh: cnh.categoria_cnh
        })
        .eq('usuario_id', user.id);

      if (error) throw error;
      
      // ✅ Atualiza os dados locais também
      setDadosMotorista(prev => ({
        ...prev,
        cnh: cnh.cnh,
        categoria_cnh: cnh.categoria_cnh
      }));
      
      toast.success('CNH atualizada com sucesso!');
      setEditandoCNH(false);
      await refreshUser();
    } catch (err) {
      console.error('Erro ao salvar CNH:', err);
      toast.error('Erro ao salvar CNH');
    }
  };

  // Handler para salvar plano de compensação
  const handleSalvarPlano = (novoPlano) => {
    const planosAtuais = getPlanosCompensacao();
    const planoComId = {
      ...novoPlano,
      id: `plano_${Date.now()}`,
      userId: user?.id,
      createdAt: new Date().toISOString()
    };
    
    planosAtuais.push(planoComId);
    
    if (savePlanosCompensacao(planosAtuais)) {
      setPlanosCompensacao(planosAtuais);
      toast.success('Plano de compensação salvo com sucesso!');
    } else {
      toast.error('Erro ao salvar plano de compensação');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logout realizado com sucesso!');
  };

  const buscarCargas = () => {
    if (!estadoSelecionado) {
      toast.error('Selecione o estado onde você está');
      return;
    }
    
    setBuscando(true);
    
    setTimeout(() => {
      setBuscando(false);
      
      if (cargasFiltradas.length > 0) {
        toast.success(`🚛 ${cargasFiltradas.length} cargas encontradas no ${estadoSelecionado}!`);
        enviarWhatsAppCargas(cargasFiltradas);
      } else {
        toast.info(`Nenhuma carga disponível no ${estadoSelecionado}${cidadeDigitada ? ` - ${cidadeDigitada}` : ''} no momento.`);
      }
    }, 800);
  };

  const enviarWhatsAppCargas = (cargas) => {
    const localizacao = cidadeDigitada.trim() 
      ? `${cidadeDigitada}/${estadoSelecionado}`
      : `Estado ${estadoSelecionado}`;
    
    const listaCargas = cargas.slice(0, 5).map(c => 
      `📍 ${c.origem_cidade}/${c.origem_estado} → ${c.destino_cidade}/${c.destino_estado}\n` +
      `💰 R$ ${c.valor?.toLocaleString() || 'N/A'}\n` +
      `🚚 ${c.tipo_veiculo || 'N/A'}\n` +
      `📦 ${c.titulo || c.tipo_carga || 'Carga'}\n` +
      `🏢 ${c.empresa_nome || 'BoraFrete'}`
    ).join('\n\n');
    
    const mensagem = encodeURIComponent(
      `🚛 *CARGAS BORAFRETE - ${estadoSelecionado}*\n\n` +
      `👤 Motorista: ${user?.nome}\n` +
      `📍 Local: ${localizacao}\n\n` +
      `*${cargas.length} CARGAS DISPONÍVEIS:*\n\n${listaCargas}\n\n` +
      `👉 Quero mais informações sobre essas cargas!`
    );
    
    window.open(`https://wa.me/${WHATSAPP_BORAFRETE}?text=${mensagem}`, '_blank');
  };

  const handleCardClick = (carga) => {
    navigate(`/carga/${carga.id}`);
  };

  const handleVerTodasCargas = () => {
    const params = new URLSearchParams();
    if (estadoSelecionado) params.set('origem', estadoSelecionado);
    if (filtroVeiculo) params.set('tipo_veiculo', filtroVeiculo);
    navigate(`/buscar-cargas?${params.toString()}`);
  };

  // Ícone dinâmico baseado no nível
  const IconNivel = nivelAtual?.id === 'ouro' || nivelAtual?.id === 'diamante' ? Trophy : 
                    nivelAtual?.id === 'black' ? Star : Award;

  // ✅ CORRIGIDO: Avatar usa foto do motorista corretamente
  const AvatarPerfil = () => {
    const imageUrl = fotoPerfil?.data;
    const temFoto = !!imageUrl;
    
    return (
      <div className="relative group">
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white">
          {temFoto ? (
            <img 
              src={imageUrl} 
              alt={isEmpresa ? "Logo da Empresa" : "Foto do Motorista"} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">
                {getIniciais(dadosMotorista.nome_completo || user?.nome || user?.razaoSocial)}
              </span>
            </div>
          )}
        </div>
        
        {/* Botão de editar foto */}
        <label 
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-md border-2 border-white" 
          title={isEmpresa ? "Alterar logo" : "Alterar foto"}
        >
          <Camera className="w-4 h-4" />
          <input 
            type="file" 
            className="hidden" 
            accept="image/jpeg,image/jpg,image/png" 
            onChange={handleFotoUpload}
            disabled={isUploadingFoto}
          />
        </label>
        
        {/* Loading indicator */}
        {isUploadingFoto && (
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
        
        {/* Botão remover foto */}
        {temFoto && (
          <button
            onClick={handleRemoverFoto}
            className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            title="Remover"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      
      {/* HEADER COM FOTO/AVATAR DO PERFIL */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* AVATAR COM FOTO */}
            <AvatarPerfil />
            
            {/* INFO DO PERFIL - ✅ USA dadosMotorista.nome_completo */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl font-bold text-slate-900">
                {isMotorista 
                  ? (dadosMotorista.nome_completo || user?.nome || 'Meu Perfil')
                  : (user?.nome || user?.razao_social || 'Meu Perfil')
                }
              </h1>
              <p className="text-slate-500 mt-1">
                {isEmpresa ? 'Empresa Transportadora' : 'Motorista'}
              </p>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 flex-wrap">
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Aprovado
                </Badge>
                {/* ✅ CORRIGIDO: CNPJ do user (Supabase) */}
                {isEmpresa && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                    <Building2 className="w-3 h-3 mr-1" />
                    CNPJ: {formatarCNPJ(user?.cnpj)}
                  </Badge>
                )}
                {/* ✅ CORRIGIDO: CPF do motorista - USA dadosMotorista.cpf */}
                {isMotorista && (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                    <Hash className="w-3 h-3 mr-1" />
                    CPF: {formatarCPF(dadosMotorista.cpf)}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* AÇÃO RÁPIDA */}
            {isEmpresa && (
              <Button 
                onClick={() => navigate('/nova-carga')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Package className="w-4 h-4 mr-2" />
                Nova Carga
              </Button>
            )}
          </div>
          
          {/* Mensagem sobre foto/logo */}
          {!fotoPerfil && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <ImageIcon className="w-5 h-5 text-blue-500" />
              <p className="text-sm text-blue-700">
                <strong>Dica:</strong> Clique no ícone da câmera no avatar para {isEmpresa ? 'adicionar seu logo' : 'adicionar sua foto'}!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FRASE MOTIVACIONAL */}
      <Card className="bg-gradient-to-r from-brand-500 to-accent-500 text-white border-none">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Zap className="w-8 h-8 flex-shrink-0" />
            <div>
              <p className="text-sm text-white/80 mb-1">Mensagem do dia</p>
              <p className="text-lg font-medium">{fraseDoDia}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PAINEL DE CONQUISTAS - APENAS MOTORISTA */}
      {isMotorista && (
        <>
          <Card className="border-2 border-brand-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-brand-500" />
                Suas Conquistas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Nível Atual */}
              <div className={`p-6 rounded-xl bg-gradient-to-r ${nivelAtual?.cor} text-white`}>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-full">
                    <IconNivel className="w-10 h-10" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/80 text-sm">Nível Atual</p>
                    <h3 className="text-2xl font-bold">{nivelAtual?.nome}</h3>
                    <p className="text-sm text-white/90">{nivelAtual?.descricao}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">{entregasRealizadas}</p>
                    <p className="text-sm text-white/80">entregas</p>
                  </div>
                </div>
              </div>

              {/* Progresso */}
              {proximoNivel && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Progresso para {proximoNivel.nome}</span>
                    <span className="font-medium">{progresso}%</span>
                  </div>
                  <Progress value={progresso} className="h-3" />
                  <p className="text-xs text-slate-500 text-center">
                    Faltam {proximoNivel.entregas - entregasRealizadas} entregas
                  </p>
                </div>
              )}

              {/* Sorteio */}
              {['ouro', 'diamante', 'black'].includes(nivelAtual?.id) ? (
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Gift className="w-6 h-6 text-green-600" />
                      <h4 className="font-bold text-green-800">🎉 Você participa do sorteio!</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-2">
                      <strong>Próximo sorteio:</strong> Dia 25
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {nivelAtual.sorteio?.map((premio, idx) => (
                        <Badge key={idx} className="bg-green-100 text-green-800">{premio}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-600">
                    🎯 Alcance o <strong>Ouro</strong> ({NIVEIS_MOTORISTA.find(n => n.id === 'ouro')?.entregas} entregas) para participar dos sorteios!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ✅ NOVO: DADOS DE CONTATO DO MOTORISTA (1 campo: telefone) */}
          <Card className="border-2 border-blue-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-6 h-6 text-blue-500" />
                Contato
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEditandoContato(!editandoContato)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editandoContato ? 'Cancelar' : 'Editar'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editandoContato ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Telefone / Celular / WhatsApp</label>
                    <Input 
                      value={contato.telefone} 
                      onChange={(e) => setContato({telefone: e.target.value})}
                      placeholder="(00) 00000-0000"
                    />
                    <p className="text-xs text-slate-400 mt-1">Este número será usado para contato e WhatsApp</p>
                  </div>
                  <Button onClick={handleSalvarContato} className="w-full bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-2" /> Salvar Telefone
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-slate-500">Telefone / WhatsApp</p>
                    <p className="font-medium">{formatarTelefone(dadosMotorista.telefone)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ✅ NOVO: ENDEREÇO DO MOTORISTA (campo único: endereco) */}
          <Card className="border-2 border-green-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Home className="w-6 h-6 text-green-500" />
                Endereço
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEditandoEndereco(!editandoEndereco)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editandoEndereco ? 'Cancelar' : 'Editar'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editandoEndereco ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Endereço (Rua/Avenida)</label>
                    <Input 
                      value={endereco.endereco} 
                      onChange={(e) => setEndereco({...endereco, endereco: e.target.value})}
                      placeholder="Rua/Avenida"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Número</label>
                      <Input value={endereco.numero} onChange={(e) => setEndereco({...endereco, numero: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Bairro</label>
                      <Input value={endereco.bairro} onChange={(e) => setEndereco({...endereco, bairro: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Cidade</label>
                      <Input value={endereco.cidade} onChange={(e) => setEndereco({...endereco, cidade: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Estado</label>
                      <Select value={endereco.estado} onValueChange={(val) => setEndereco({...endereco, estado: val})}>
                        <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>
                          {ESTADOS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">CEP</label>
                    <Input 
                      value={endereco.cep} 
                      onChange={(e) => setEndereco({...endereco, cep: e.target.value})} 
                      placeholder="00000-000" 
                    />
                  </div>
                  <Button onClick={handleSalvarEndereco} className="w-full bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" /> Salvar Endereço
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">
                        {dadosMotorista.endereco ? `${dadosMotorista.endereco}, ${dadosMotorista.numero}` : 'Endereço não informado'}
                      </p>
                      {dadosMotorista.bairro && <p className="text-sm text-slate-500">{dadosMotorista.bairro}</p>}
                      <p className="text-sm text-slate-500">
                        {dadosMotorista.cidade && dadosMotorista.estado ? `${dadosMotorista.cidade} - ${dadosMotorista.estado}` : ''}
                        {dadosMotorista.cep && ` • CEP: ${formatarCEP(dadosMotorista.cep)}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ✅ NOVO: CNH DO MOTORISTA */}
          <Card className="border-2 border-purple-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-purple-500" />
                CNH
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEditandoCNH(!editandoCNH)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editandoCNH ? 'Cancelar' : 'Editar'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editandoCNH ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Número da CNH</label>
                    <Input 
                      value={cnh.cnh} 
                      onChange={(e) => setCnh({...cnh, cnh: e.target.value})}
                      placeholder="00000000000"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Categoria</label>
                    <Select value={cnh.categoria_cnh} onValueChange={(val) => setCnh({...cnh, categoria_cnh: val})}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'].map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleSalvarCNH} className="w-full bg-purple-600 hover:bg-purple-700">
                    <Save className="w-4 h-4 mr-2" /> Salvar CNH
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <CreditCard className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">CNH</p>
                      <p className="font-medium">{dadosMotorista.cnh || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <FileCheck className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Categoria</p>
                      <p className="font-medium">{dadosMotorista.categoria_cnh || 'Não informado'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ✅ NOVO: CAMINHÃO DO MOTORISTA (1 placa no Supabase) */}
          <Card className="border-2 border-orange-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-6 h-6 text-orange-500" />
                Meu Caminhão
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEditandoCaminhao(!editandoCaminhao)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editandoCaminhao ? 'Cancelar' : 'Editar'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editandoCaminhao ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Placa do Caminhão</label>
                    <Input 
                      value={caminhao.placa_caminhao} 
                      onChange={(e) => setCaminhao({placa_caminhao: e.target.value.toUpperCase()})}
                      placeholder="ABC-1234"
                    />
                  </div>
                  <Button onClick={handleSalvarCaminhao} className="w-full bg-orange-600 hover:bg-orange-700">
                    <Save className="w-4 h-4 mr-2" /> Salvar Placa
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Truck className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-500">Placa do Caminhão</p>
                    <p className="font-medium">{formatarPlaca(dadosMotorista.placa_caminhao)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* BUSCAR CARGAS */}
          <Card className="border-2 border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LocateFixed className="w-6 h-6 text-orange-500" />
                Onde você está agora?
              </CardTitle>
              <CardDescription>
                Selecione o estado e veja cargas reais do sistema BoraFrete
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!showLocalizacao ? (
                <Button 
                  className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-lg"
                  onClick={() => setShowLocalizacao(true)}
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  DEFINIR LOCALIZAÇÃO
                </Button>
              ) : (
                <div className="space-y-4">
                  {/* Estado */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Estado *</label>
                    <Select value={estadoSelecionado} onValueChange={setEstadoSelecionado}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Selecione o estado" />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADOS.map(uf => (
                          <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cidade */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Cidade (opcional)</label>
                    <Input
                      placeholder="Digite sua cidade"
                      value={cidadeDigitada}
                      onChange={(e) => setCidadeDigitada(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  {/* Filtro Veículo */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tipo de Veículo (opcional)</label>
                    <Select 
                      value={filtroVeiculo || "todos"}
                      onValueChange={(val) => setFiltroVeiculo(val === "todos" ? "" : val)}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Todos os tipos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os tipos</SelectItem>
                        {TIPOS_VEICULO.map(tipo => (
                          <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowLocalizacao(false);
                        setEstadoSelecionado('');
                        setCidadeDigitada('');
                        setFiltroVeiculo('');
                        setCargasFiltradas([]);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      className="flex-1 bg-green-500 hover:bg-green-600 h-12"
                      onClick={buscarCargas}
                      disabled={!estadoSelecionado || buscando || loadingCargas}
                    >
                      {buscando || loadingCargas ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> BUSCAR CARGAS</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* RESULTADO: Cargas Reais */}
              {estadoSelecionado && (
                <div className="mt-6">
                  {loadingCargas ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-brand-600 mr-2" />
                      <span className="text-slate-600">Carregando cargas do sistema...</span>
                    </div>
                  ) : cargasFiltradas.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-slate-800 flex items-center gap-2">
                          <Truck className="w-5 h-5 text-brand-600" />
                          Cargas em {estadoSelecionado}
                          {cidadeDigitada && ` - ${cidadeDigitada}`}
                        </h4>
                        <Badge className="bg-brand-100 text-brand-800">
                          {cargasFiltradas.length} encontradas
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <AnimatePresence>
                          {cargasFiltradas.slice(0, 4).map((carga, index) => (
                            <motion.div
                              key={carga.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                            >
                              <CargaCard 
                                carga={carga}
                                user={user}
                                index={index}
                                onClick={() => handleCardClick(carga)}
                              />
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>

                      {cargasFiltradas.length > 4 && (
                        <Button 
                          variant="outline" 
                          className="w-full border-brand-200 text-brand-600 hover:bg-brand-50"
                          onClick={handleVerTodasCargas}
                        >
                          Ver todas as {cargasFiltradas.length} cargas
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}

                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3 mb-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-medium text-green-800">
                            Cargas enviadas para o WhatsApp!
                          </p>
                        </div>
                        <p className="text-xs text-green-600">
                          Aguarde contato da BoraFrete ou fale diretamente: (65) 98118-1997
                        </p>
                      </div>
                    </div>
                  ) : estadoSelecionado ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <Package className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                      <h4 className="font-medium text-slate-700 mb-1">
                        Nenhuma carga encontrada
                      </h4>
                      <p className="text-sm text-slate-500 mb-4">
                        Tente ajustar os filtros ou selecionar outro estado
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setCidadeDigitada('');
                          setFiltroVeiculo('');
                        }}
                      >
                        Limpar filtros
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* DADOS DO PERFIL */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-6 h-6 text-brand-500" />
            Meus Dados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-500">Nome</label>
              <p className="font-medium">
                {isMotorista 
                  ? (dadosMotorista.nome_completo || user?.nome || 'Não informado')
                  : (user?.nome || 'Não informado')
                }
              </p>
            </div>
            <div>
              <label className="text-sm text-slate-500">Email</label>
              <p className="font-medium">{user?.email}</p>
            </div>
            {/* ✅ CORRIGIDO: CNPJ do user (Supabase) */}
            {isEmpresa && (
              <div>
                <label className="text-sm text-slate-500">CNPJ</label>
                <p className="font-medium">{formatarCNPJ(user?.cnpj)}</p>
              </div>
            )}
            {/* ✅ CORRIGIDO: CPF do motorista - USA dadosMotorista.cpf */}
            {isMotorista && (
              <div>
                <label className="text-sm text-slate-500">CPF</label>
                <p className="font-medium">{formatarCPF(dadosMotorista.cpf)}</p>
              </div>
            )}
            <div>
              <label className="text-sm text-slate-500">Tipo</label>
              <p className="font-medium capitalize">{user?.tipo || 'Motorista'}</p>
            </div>
            <div>
              <label className="text-sm text-slate-500">Status</label>
              <p className="font-medium text-green-600">Aprovado ✓</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AÇÕES */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {isMotorista && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/painel')}
                  className="flex-1 bg-brand-50 border-brand-200 text-brand-700 hover:bg-brand-100"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Ir para Painel
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/meus-veiculos')}
                  className="flex-1"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Meus Veículos
                </Button>
              </>
            )}
            
            {isEmpresa && (
              <Button 
                variant="outline" 
                onClick={() => navigate('/minhas-cargas')}
                className="flex-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <Package className="w-4 h-4 mr-2" />
                Minhas Cargas
              </Button>
            )}
            
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              className="flex-1"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Perfil;